import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingTable = () => {
  const pricingTiers = [
    {
      id: 'basic',
      name: 'Basic Care',
      description: 'Essential laundry services for everyday needs',
      price: '149',
      unit: 'per kg',
      features: [
        'Machine wash & dry',
        'Basic folding',
        '48-hour turnaround',
        'Standard detergent',
        'Basic stain removal'
      ],
      buttonText: 'Choose Basic',
      isPopular: false
    },
    {
      id: 'premium',
      name: 'Premium Care',
      description: 'Enhanced services with premium treatment',
      price: '249',
      unit: 'per kg',
      features: [
        'Premium wash & dry',
        'Professional pressing',
        '24-hour turnaround',
        'Eco-friendly detergent',
        'Advanced stain removal',
        'Fabric softener included',
        'Quality inspection'
      ],
      buttonText: 'Choose Premium',
      isPopular: true
    },
    {
      id: 'luxury',
      name: 'Luxury Care',
      description: 'Ultimate care for your finest garments',
      price: '399',
      unit: 'per kg',
      features: [
        'Hand wash for delicates',
        'Expert pressing & finishing',
        'Same-day service',
        'Premium eco detergent',
        'Specialized stain treatment',
        'Fabric protection spray',
        'Individual garment care',
        'Premium packaging'
      ],
      buttonText: 'Choose Luxury',
      isPopular: false
    }
  ];

  const handleSelectPlan = (planId) => {
    window.location.href = '/contact-us';
  };

  return (
    <div id="pricing-section" className="space-y-8">
      {/* Section Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-foreground mb-4">
          Transparent Pricing
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Choose the perfect care level for your garments. All prices include pickup and delivery within Coimbatore city limits.
        </p>
      </motion.div>
      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-8">
        {pricingTiers?.map((tier, index) => (
          <motion.div
            key={tier?.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className={`relative bg-card rounded-2xl p-8 border transition-all duration-300 hover:shadow-elevation-3 ${
              tier?.isPopular 
                ? 'border-primary shadow-elevation-2 transform scale-105' 
                : 'border-border hover:border-primary'
            }`}
          >
            {/* Popular Badge */}
            {tier?.isPopular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold">
                  Most Popular
                </div>
              </div>
            )}

            {/* Tier Header */}
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-foreground mb-2">{tier?.name}</h3>
              <p className="text-muted-foreground text-sm mb-6">{tier?.description}</p>
              
              <div className="space-y-2">
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-bold text-primary">₹{tier?.price}</span>
                  <span className="text-muted-foreground ml-2">{tier?.unit}</span>
                </div>
              </div>
            </div>

            {/* Features List */}
            <div className="space-y-4 mb-8">
              {tier?.features?.map((feature, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-success/20 flex items-center justify-center mt-0.5">
                    <Icon name="Check" size={12} color="var(--color-success)" />
                  </div>
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <Button
              variant={tier?.isPopular ? "default" : "outline"}
              fullWidth
              onClick={() => handleSelectPlan(tier?.id)}
              className="transition-all duration-300 hover:shadow-elevation-2"
            >
              {tier?.buttonText}
            </Button>
          </motion.div>
        ))}
      </div>
      {/* Additional Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        viewport={{ once: true }}
        className="bg-muted/50 rounded-2xl p-6 text-center"
      >
        <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={16} color="var(--color-success)" />
            <span>100% Quality Guarantee</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Truck" size={16} color="var(--color-primary)" />
            <span>Free Pickup & Delivery</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} color="var(--color-accent)" />
            <span>On-Time Promise</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PricingTable;